import React from "react"
import {
    Avatar,
    Box,
    Button,
    componentStyles,
    Highlight,
    HorizontalStack,
    Spacer,
    Text,
    TextSecondaryExtraSmall,
    useToast,
} from "@mallardbay/lib-react-components"

import { useStartImpersonationMutation } from "~graphql/auth.generated"
import { usePersistedPortalEntities } from "~utils/hooks/use-persisted-portal-entities"
import { navigateWithHardReload } from "~utils/helpers/navigation-helpers"
import { formatTeamMemberRole } from "~utils/helpers/team-member-helpers"
import { getFullName } from "~utils/helpers/string-helpers"
import type {
    ImpersonationMember,
    ImpersonationSearchResult,
} from "~utils/hooks/use-search-outfitters-for-impersonation"
import COPY from "~config/copy-constants"

interface Props {
    readonly result: ImpersonationSearchResult
    readonly query: string
}

export default function OutfitterImpersonationItem({ result, query }: Props) {
    const styles = useStyles()

    if (result.auto_select_member) {
        return (
            <ImpersonationMemberItem
                member={result.auto_select_member}
                query={query}
                subtitle={result.outfitter.name}
            />
        )
    }

    return (
        <Box style={styles.outfitterContainer}>
            <HorizontalStack style={styles.outfitterRow}>
                <Avatar
                    size="sm"
                    style={styles.avatar}
                    src={result.outfitter.avatar?.url ?? ""}
                    name={result.outfitter.name}
                />
                <Box>
                    <Text numberOfLines={1} style={styles.highlightName}>
                        <Highlight query={query} style={styles.highlight}>
                            {result.outfitter.name}
                        </Highlight>
                    </Text>
                    <TextSecondaryExtraSmall numberOfLines={1}>
                        {COPY.SELECT_A_MEMBER}
                    </TextSecondaryExtraSmall>
                </Box>
            </HorizontalStack>
            <Box style={styles.membersList}>
                {result.members.map((member) => (
                    <ImpersonationMemberItem
                        key={member.user.id}
                        member={member}
                        query={query}
                        subtitle={formatTeamMemberRole(member.role)}
                    />
                ))}
            </Box>
        </Box>
    )
}

interface ImpersonationMemberItemProps {
    readonly member: ImpersonationMember
    readonly query: string
    readonly subtitle: string
}

// eslint-disable-next-line react/no-multi-comp -- TODO: Extract component to separate file
function ImpersonationMemberItem({
    member,
    query,
    subtitle,
}: ImpersonationMemberItemProps) {
    const { clearAllPersistedPortalEntities } = usePersistedPortalEntities()
    const { showError } = useToast()
    const styles = useStyles()
    const [startImpersonation, { loading: isStarting }] =
        useStartImpersonationMutation({
            variables: {
                actsAsUserId: member.user.id,
            },
            onCompleted: () => {
                clearAllPersistedPortalEntities()
                navigateWithHardReload()
            },
            onError: showError,
        })

    return (
        <HorizontalStack style={styles.memberRow}>
            <Avatar
                style={styles.avatar}
                src={member.user.avatar_url ?? ""}
                name={getFullName(member.user)}
            />
            <Box>
                <Text numberOfLines={1} style={styles.highlightName}>
                    <Highlight query={query} style={styles.highlight}>
                        {getFullName(member.user)}
                    </Highlight>
                </Text>
                <TextSecondaryExtraSmall numberOfLines={1}>
                    <Highlight query={query} style={styles.highlight}>
                        {subtitle}
                    </Highlight>
                </TextSecondaryExtraSmall>
            </Box>
            <Spacer />
            <Button
                size="sm"
                isLoading={isStarting}
                onClick={() => {
                    void startImpersonation()
                }}
            >
                {COPY.VIEW_AS}
            </Button>
        </HorizontalStack>
    )
}

function useStyles() {
    return componentStyles({
        outfitterContainer: {
            py: 2,
        },
        outfitterRow: {
            px: 2,
            gap: 3,
        },
        membersList: {
            mt: 2,
            pl: 6,
        },
        memberRow: {
            py: 2,
            px: 2,
            gap: 3,
            rounded: "xl",
        },
        avatar: {
            shadow: "lg",
        },
        highlightName: {
            fontSize: "15px",
            fontWeight: "bold",
        },
        highlight: {
            bg: "yellow.200",
        },
    })
}
